import React, { useDeferredValue, useMemo, useState } from 'react'
import { Input, Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from 'dui'

import { Table, TableHeader, TableBody, TableRow, TableCell } from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { QuestionDialog } from '@/components/questions/question-dialog'
import {
  useCreateQuestionMutation,
  useDeleteQuestionMutation,
  useGetCategoriesQuery,
  useGetQuestionsQuery,
  useUpdateQuestionMutation,
} from '@/store/question-api'
import { ConfirmDialog } from '@/components/questions/confirm-dialog'
import { formatEmptyLabel } from '@/utils'

import type { Question } from '@/store/question-api'

type DialogMode = 'add' | 'edit' | 'view'

const Questions: React.FC = () => {
  const { data = [], refetch } = useGetQuestionsQuery()
  const [createQuestion] = useCreateQuestionMutation()
  const [updateQuestion] = useUpdateQuestionMutation()
  const [deleteQuestion] = useDeleteQuestionMutation()

  const [dialogData, setDialogData] = useState<Question | null>(null)
  const [dialogMode, setDialogMode] = useState<DialogMode | null>(null)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)
  const [query, setQuery] = useState<string>('')
  const { data: categories = [] } = useGetCategoriesQuery()
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const q = useMemo(() => query.trim().toLowerCase(), [query])
  const deferredQ = useDeferredValue(q)

  const categoriesById = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories])
  const filtered = useMemo(() => {
    return data.filter((x) => {
      const matchesText =
        !deferredQ ||
        x.question?.toLowerCase().includes(deferredQ) ||
        x.answer?.toLowerCase().includes(deferredQ) ||
        x.category?.name?.toLowerCase().includes(deferredQ)

      const matchesCategory = categoryFilter === 'all' || x.category?.id === categoryFilter
      return matchesText && matchesCategory
    })
  }, [data, deferredQ, categoryFilter])

  const emptyLabel = useMemo(() => {
    return formatEmptyLabel(filtered.length, query.trim(), categoryFilter, categoriesById)
  }, [filtered.length, query, categoryFilter, categoriesById])

  const closeDialog = (): void => {
    setDialogData(null)
    setDialogMode(null)
  }

  const handleDelete = async (id: string): Promise<void> => {
    await deleteQuestion(id)
    await refetch()
  }

  const handleSave = async (updated: Question): Promise<void> => {
    const categoryId = updated.category?.id ?? null

    if (dialogMode === 'edit') {
      await updateQuestion({ id: updated.id, question: updated.question, answer: updated.answer, categoryId })
    } else if (dialogMode === 'add') {
      await createQuestion({ question: updated.question, answer: updated.answer, categoryId })
    }
    await refetch()
    closeDialog()
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4 w-full sm:w-auto">
          <h2 className="text-2xl font-semibold">Pitanja</h2>
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Pretraži pitanja i odgovore…"
            className="w-full sm:w-72"
          />
          <div className="w-56">
            <Select value={categoryFilter} onValueChange={(id) => setCategoryFilter(id)}>
              <SelectTrigger>
                <SelectValue placeholder="Sve kategorije" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Sve kategorije</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button
          onClick={() => {
            setDialogData({ id: '', question: '', answer: '' })
            setDialogMode('add')
          }}
        >
          Novo pitanje
        </Button>
      </div>

      <Table className="table-fixed">
        <TableHeader>
          <TableRow>
            <TableCell className="w-1/3">Pitanje</TableCell>
            <TableCell className="w-1/2">Odgovor</TableCell>
            <TableCell className="w-1/6">Kategorija</TableCell>
            <TableCell className="w-1/6">Akcije</TableCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.map((item) => (
            <TableRow
              key={item.id}
              className="cursor-pointer"
              onClick={() => {
                setDialogData(item)
                setDialogMode('view')
              }}
            >
              <TableCell className="w-1/3 truncate whitespace-nowrap overflow-hidden">
                {item.question}
              </TableCell>
              <TableCell className="w-1/2 truncate whitespace-nowrap overflow-hidden">
                {item.answer}
              </TableCell>
              <TableCell className="w-1/6 truncate">{item.category?.name ?? '—'}</TableCell>
              <TableCell className="w-1/6 flex gap-2 items-center whitespace-nowrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation()
                    setDialogData(item)
                    setDialogMode('edit')
                  }}
                >
                  Izmeni
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation()
                    setDeleteConfirmId(item.id)
                  }}
                >
                  Obriši
                </Button>
              </TableCell>
            </TableRow>
          ))}
          {filtered.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} className="py-6 text-center text-sm text-muted-foreground">
                {emptyLabel}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {dialogData && dialogMode && (
        <QuestionDialog
          open={!!dialogData}
          onClose={closeDialog}
          data={dialogData}
          mode={dialogMode}
          onSave={handleSave}
          readOnly={dialogMode === 'view'}
        />
      )}
      {deleteConfirmId && (
        <ConfirmDialog
          open={!!deleteConfirmId}
          title="Potvrda brisanja"
          description="Da li ste sigurni da želite da obrišete ovo pitanje?"
          confirmLabel="Obriši"
          confirmVariant="destructive"
          onConfirm={async () => {
            await handleDelete(deleteConfirmId)
            setDeleteConfirmId(null)
          }}
          onCancel={() => setDeleteConfirmId(null)}
        />
      )}
    </div>
  )
}

export default Questions
